// workflow-frame-bus.ts — Module-level pub/sub bus for workflow run frames.
//
// Dispatch handlers publish frames here (keyed by run_id) so hooks can
// subscribe by runId without going through React Context. This avoids the
// re-render storm that would occur if RealtimeContext carried a frames stream
// (every token_delta would trigger a context-wide re-render).
//
// scout-verified: RealtimeProvider exposes only {status, lastEventId, reconnect}
// (realtime-provider.tsx:31); no frames API exists → bus singleton required.
// Design locked: red-team Q15 / C3.
import type { Frame } from "./types";

type FrameHandler = (frame: Frame) => void;

const subscribers: Map<string, Set<FrameHandler>> = new Map();

export const workflowFrameBus = {
  /**
   * Subscribe to frames for a specific runId.
   * Returns an unsubscribe function to call on cleanup.
   */
  subscribe(runId: string, handler: FrameHandler): () => void {
    let set = subscribers.get(runId);
    if (!set) {
      set = new Set();
      subscribers.set(runId, set);
    }
    set.add(handler);
    return () => {
      const s = subscribers.get(runId);
      if (!s) return;
      s.delete(handler);
      if (s.size === 0) subscribers.delete(runId);
    };
  },

  /** Publish a frame to all subscribers for runId. */
  publish(runId: string, frame: Frame): void {
    subscribers.get(runId)?.forEach((h) => h(frame));
  },

  /** Test-only: clear all subscriptions between test cases. */
  _resetForTests(): void {
    subscribers.clear();
  },
};
